// Shared share-card data shaping (2026-08-18 lift, consolidation-reviewer
// finding): both hosts' PostSessionSummaryCard need the identical dateLabel
// formatting + i18n label mapping + sessionResultToShareCardData() call —
// this used to be two hand-copied memos (mobile + web-rnw) that could drift
// on the next stat-strip change. Same "2+ callsite → build the primitive"
// trigger useShareCardHandle.ts already followed for the capture+share step.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { weightUnitLabels } from '../weightUnitLabels';

import { useDateLocale } from '../primitives/DateFormatContext';
import {
  dateFormatter,
  estimateLabelKey,
  sessionResultToShareCardData,
  type HeatmapRamp,
  type SessionResult,
  type ShareCardData,
  type WorkoutEnergyReadout,
} from '@heraion/shared';

export function useShareCardData(input: {
  result: SessionResult;
  socialHandle: string | null;
  unit: 'kg' | 'lb';
  locale: string;
  /** Resolved energy readout (calories + effort/HR); null when nothing
   *  estimable — the Calories/Effort stat cells are simply omitted. */
  energy?: WorkoutEnergyReadout | null;
  /** (slug, snapshotFallback) → localised display name — the caller's FK
   *  resolver, per [[feedback_no_denormalized_display_snapshots]]. */
  resolveName: (slug: string, snapshotFallback?: string) => string;
  /** The mount's `theme.heatmapRamp` — the figures in the PNG are painted with
   *  it. Required (DS-77): the card used to fall back to the pinned orange
   *  ramp, so it shipped the old family under every theme. */
  heatmapRamp: HeatmapRamp;
}): { shareData: ShareCardData; dateLabel: string } {
  const {
    result,
    socialHandle,
    unit,
    locale,
    energy = null,
    resolveName,
    heatmapRamp,
  } = input;
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  // Pre-format the date here so the off-screen renderer stays locale-agnostic.
  const dateLabel = useMemo(() => {
    return dateFormatter(dateLocale, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(result.computedAt));
  }, [dateLocale, result.computedAt]);

  const shareLabels = useMemo(
    () => ({
      durationMinute: t('mobile.share.durationMinute'),
      durationHour: t('mobile.share.durationHour'),
      footerDensitySuffix: t('mobile.share.footerDensitySuffix'),
      fallbackLabel: t('mobile.share.fallbackLabel'),
      timeLabel: t('mobile.share.timeLabel'),
      tutLabel: t('mobile.share.tutLabel'),
      impulseUnit: t('mobile.share.impulseUnit'),
      e1rmSuffix: t('mobile.share.e1rmSuffix'),
      // Stat-strip cell titles (HC-8 B3) — reuse the in-app postSession
      // block keys so no new i18n strings are minted.
      volumeLabel: t('mobile.postSession.blockVolume'),
      durationLabel: t('mobile.postSession.blockDuration'),
      densityLabel: t('mobile.postSession.blockDensity'),
      impulseLabel: t('mobile.share.impulseLabel'),
      caloriesLabel: t('mobile.postSession.blockCalories'),
      // #290: the «estimate» tag beside the Calories value. The card only
      // ever shows a MET figure (see buildFooterStats), always low
      // confidence, so the rough variant is the honest one.
      estimateTag: t(estimateLabelKey('low')),
      // ⛔ CN-26 ondata 2. Senza questa riga il merge di `shareCard.ts`
      // (`{ ...DEFAULT_SHARE_CARD_LABELS, ...input.labels }`) faceva ricadere
      // l'unità su `UNTRANSLATED_UNIT_LABELS`, e la card è la superficie più
      // PUBBLICA che abbiamo: un PNG che l'atleta posta. Un russo avrebbe
      // condiviso un'immagine che dice «lb». Trovato dalla REVIEW, non da me.
      units: weightUnitLabels(t),
    }),
    [t],
  );

  const shareData = useMemo(
    () =>
      sessionResultToShareCardData({
        result,
        socialHandle,
        unit,
        locale,
        dateLabel,
        resolveName,
        labels: shareLabels,
        heatmapRamp,
        // Static image → the estimate, no (i) pill (B3).
        // No Effort cell (LEGAL-5, 2026-09-09): it was always a heart-rate
        // figure, and HR has exactly one source — Health Connect — so it can
        // never be an ours-only cell on an artifact that leaves the app.
        energy: energy
          ? {
              kcal: energy.estimate?.kcal ?? null,
              // LEGAL-5: lets the formatter swap in the MET twin when the
              // estimate is computed FROM an HC heart rate (method "keytel",
              // see calorieEstimate.ts).
              kcalMethod: energy.estimate?.method ?? null,
              // LEGAL-5 ripiego (2026-09-09): the caller's MET-only twin —
              // used by the strip in place of kcal when the figure above
              // touches Health Connect. null/absent when the caller had no
              // raw per-set ingredients to recompute it.
              metFallbackKcal: energy.metFallbackKcal ?? null,
            }
          : undefined,
      }),
    [
      result,
      socialHandle,
      unit,
      locale,
      dateLabel,
      resolveName,
      shareLabels,
      energy,
      heatmapRamp,
    ],
  );

  return { shareData, dateLabel };
}
